/**
 * EditExternalAccountModal - Bridge-style external account edit modal (Fullscreen)
 * 
 * Features:
 * - View read-only account info (account number, bank name, owner)
 * - Edit beneficiary address fields
 * - Activate/Deactivate account
 * - Delete account
 * 
 * Design: Fullscreen Sheet from bottom (95vh) on all devices
 * @see MODAL_FULLSCREEN_ADAPTATION_PLAN.md
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { ExternalAccount } from '@/features/user/wallet/hooks/useWallet';
import { supabaseBrowser } from '@/lib/supabase-browser';
import { callEdgeFunction } from '@/lib/rpc/utils/edgeFunctions';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { useQueryClient } from '@tanstack/react-query';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';
import { Power, PowerOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from '@/hooks/use-toast';

interface EditExternalAccountModalProps {
  open: boolean;
  onClose: () => void;
  account: ExternalAccount | null;
  onAccountUpdated?: () => void;
}

interface AddressFormData {
  street_line_1: string;
  street_line_2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export function EditExternalAccountModal({
  open,
  onClose,
  account,
  onAccountUpdated,
}: EditExternalAccountModalProps) {
  const queryClient = useQueryClient();
  const { data: isAdmin } = useIsAdmin();

  // Form state
  const [addressForm, setAddressForm] = useState<AddressFormData>({
    street_line_1: '',
    street_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
  });

  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Confirmation dialogs
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  // Initialize form when account changes
  useEffect(() => {
    if (account) {
      setAddressForm({
        street_line_1: '',
        street_line_2: '',
        city: '',
        state: '',
        postal_code: '',
        country: account.country || '',
      });
    }
  }, [account]);

  // Guard: early return if no account
  if (!open || !account) {
    return null;
  }

  const isActive = account.status === 'active';

  // Handle form field change
  const handleFieldChange = (field: keyof AddressFormData, value: string) => {
    setAddressForm(prev => ({ ...prev, [field]: value }));
  };

  // Save address changes via Edge Function (works for all users)
  const handleSave = async () => {
    if (!account?.id) return;

    setIsSaving(true);
    try {
      const result = await callEdgeFunction('external-account-update-address', {
        account_id: account.id,
        address: addressForm,
      });

      if (!result.ok) {
        throw new Error(result.error?.message || 'Failed to update address');
      }

      toast.success('Address updated successfully');
      queryClient.invalidateQueries({ queryKey: ['external-accounts'] });
      onAccountUpdated?.();
      onClose();
    } catch (error: any) {
      console.error('[EditExternalAccountModal] Save error:', error);
      toast.error(error.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle active/inactive status
  const handleToggleStatus = async () => {
    if (!account?.id) return;

    setIsToggling(true);
    try {
      const newStatus = isActive ? 'inactive' : 'active';

      const { error } = await supabaseBrowser
        .from('external_accounts')
        .update({
          status: newStatus,
          active: newStatus === 'active',
          updated_at: new Date().toISOString(),
        })
        .eq('id', account.id);

      if (error) throw error;

      toast.success(`Account ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['external-accounts'] });
      onAccountUpdated?.();
      setShowDeactivateConfirm(false);
      onClose();
    } catch (error: any) {
      console.error('[EditExternalAccountModal] Toggle status error:', error);
      toast.error(error.message || 'Failed to update status');
    } finally {
      setIsToggling(false);
    }
  };

  // Delete account (soft delete)
  const handleDelete = async () => {
    if (!account?.id) return;

    setIsDeleting(true);
    try {
      const { error } = await supabaseBrowser
        .from('external_accounts')
        .update({
          status: 'deleted',
          active: false,
          updated_at: new Date().toISOString(),
        })
        .eq('id', account.id);

      if (error) throw error;

      toast.success('Account deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['external-accounts'] });
      onAccountUpdated?.();
      setShowDeleteConfirm(false);
      onClose();
    } catch (error: any) {
      console.error('[EditExternalAccountModal] Delete error:', error);
      toast.error(error.message || 'Failed to delete account');
    } finally {
      setIsDeleting(false);
    }
  };

  // Format masked account number
  const getMaskedAccountNumber = () => {
    if (account.account_number) {
      return `****${account.account_number.slice(-4)}`;
    }
    if (account.account_last4) {
      return `****${account.account_last4}`;
    }
    if (account.iban) {
      return `****${account.iban.slice(-4)}`;
    }
    return '****';
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent
          side="bottom"
          className={cn(
            'h-[95vh]', // Fullscreen from bottom (95% viewport height)
            semanticTokens.motion.standard
          )}
        >
          {/* Header */}
          <SheetHeader className="flex-shrink-0 pb-4 border-b">
            <div className="flex items-center gap-3">
              <div className={cn('p-2.5 rounded-xl', semanticTokens.bg.muted)}>
                <SFIcon icon={sfIcons.card} className="h-5 w-5 text-primary" aria-hidden="true" />
              </div>
              <div>
                <SheetTitle className={semanticTokens.typography.h3}>
                  Edit bank account
                </SheetTitle>
                <SheetDescription className={cn(semanticTokens.typography.caption, 'mt-0.5')}>
                  Update billing address for your linked account
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto py-6 space-y-6">
            {/* Read-only Account Info */}
            <div className={cn(
              "p-4 rounded-xl space-y-4",
              semanticTokens.bg.muted
            )}>
              <p className={cn("text-sm", semanticTokens.fg.muted)}>
                The following information cannot be edited:
              </p>

              <div className="space-y-3">
                {/* Account Number */}
                <div>
                  <Label className={cn("text-xs", semanticTokens.fg.muted)}>Account number</Label>
                  <p className={cn("font-mono", semanticTokens.typography.body)}>
                    {getMaskedAccountNumber()}
                  </p>
                </div>

                {/* Bank Name */}
                <div>
                  <Label className={cn("text-xs", semanticTokens.fg.muted)}>Bank name</Label>
                  <p className={semanticTokens.typography.body}>
                    {account.bank_name || 'Not available'}
                  </p>
                </div>

                {/* Account Owner */}
                <div>
                  <Label className={cn("text-xs", semanticTokens.fg.muted)}>Account owner</Label>
                  <p className={semanticTokens.typography.body}>
                    {account.account_owner_name || 'Not available'}
                  </p>
                </div>
              </div>
            </div>

            {/* Editable Address Fields - All users can edit their billing address */}
            <Separator />
            <div className="space-y-4">
              <p className={cn("text-sm", semanticTokens.fg.muted)}>
                Edit billing address:
              </p>
                  {/* Street Line 1 */}
                  <div className="space-y-2">
                    <Label htmlFor="street_line_1" className="flex items-center gap-1">
                      Street line 1
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="street_line_1"
                      placeholder="e.g. 123 Main St"
                      value={addressForm.street_line_1}
                      onChange={(e) => handleFieldChange('street_line_1', e.target.value)}
                      className={cn('min-h-[52px] rounded-xl', semanticTokens.focus.ring)}
                    />
                  </div>

                  {/* Street Line 2 */}
                  <div className="space-y-2">
                    <Label htmlFor="street_line_2">Street line 2</Label>
                    <Input
                      id="street_line_2"
                      placeholder="e.g. Unit 500"
                      value={addressForm.street_line_2}
                      onChange={(e) => handleFieldChange('street_line_2', e.target.value)}
                      className={cn('min-h-[52px] rounded-xl', semanticTokens.focus.ring)}
                    />
                  </div>

                  {/* City & State */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="city" className="flex items-center gap-1">
                        City
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="city"
                        placeholder="e.g. Baltimore"
                        value={addressForm.city}
                        onChange={(e) => handleFieldChange('city', e.target.value)}
                        className={cn('min-h-[52px] rounded-xl', semanticTokens.focus.ring)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state" className="flex items-center gap-1">
                        State
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="state"
                        placeholder="e.g. MD"
                        value={addressForm.state}
                        onChange={(e) => handleFieldChange('state', e.target.value)}
                        className={cn('min-h-[52px] rounded-xl', semanticTokens.focus.ring)}
                      />
                    </div>
                  </div>

                  {/* Postal Code & Country */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="postal_code" className="flex items-center gap-1">
                        Postal code
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="postal_code"
                        placeholder="e.g. 21201"
                        value={addressForm.postal_code}
                        onChange={(e) => handleFieldChange('postal_code', e.target.value)}
                        className={cn('min-h-[52px] rounded-xl', semanticTokens.focus.ring)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country" className="flex items-center gap-1">
                        Country
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="country"
                        placeholder="e.g. US, DE"
                        value={addressForm.country}
                        onChange={(e) => handleFieldChange('country', e.target.value.toUpperCase())}
                        className={cn('min-h-[52px] rounded-xl', semanticTokens.focus.ring)}
                        maxLength={2}
                      />
                    </div>
                  </div>
            </div>

            {/* Account Actions - Admin only */}
            {isAdmin && (
              <>
                <Separator />
                <div className="space-y-3">
                  <Label className={semanticTokens.fg.muted}>Account actions (Admin)</Label>

                  <div className="flex flex-wrap gap-2">
                    {/* Toggle Active/Inactive */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (isActive) {
                          setShowDeactivateConfirm(true);
                        } else {
                          handleToggleStatus();
                        }
                      }}
                      disabled={isToggling}
                      className={cn(
                        "gap-2 min-h-[48px] rounded-xl",
                        isActive
                          ? "text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          : "text-green-600 hover:text-green-700 hover:bg-green-50"
                      )}
                    >
                      {isToggling ? (
                        <InlineLoadingIndicator size="sm" aria-hidden="true" />
                      ) : isActive ? (
                        <PowerOff className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <Power className="h-4 w-4" aria-hidden="true" />
                      )}
                      {isActive ? 'Deactivate' : 'Activate'}
                    </Button>

                    {/* Delete Account */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isDeleting}
                      className="gap-2 min-h-[48px] rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      {isDeleting ? (
                        <InlineLoadingIndicator size="sm" aria-hidden="true" />
                      ) : (
                        <SFIcon icon={sfIcons.delete} className="h-4 w-4" aria-hidden="true" />
                      )}
                      Delete
                    </Button>
                  </div>

                  {/* Status Badge */}
                  <div className="flex items-center gap-2 pt-2">
                    <span className={cn("text-sm", semanticTokens.fg.muted)}>Current status:</span>
                    <Badge
                      variant={isActive ? 'default' : 'secondary'}
                      className={cn(
                        isActive
                          ? cn(semanticTokens.positive.bg, semanticTokens.positive.fg)
                          : cn(semanticTokens.warning.bg, semanticTokens.warning.fg)
                      )}
                    >
                      {account.status || 'Unknown'}
                    </Badge>
                  </div>
                </div>
              </>
            )}

            {/* Status Badge for non-admin users */}
            {!isAdmin && (
              <div className="flex items-center gap-2 pt-4">
                <span className={cn("text-sm", semanticTokens.fg.muted)}>Current status:</span>
                <Badge
                  variant={isActive ? 'default' : 'secondary'}
                  className={cn(
                    isActive
                      ? cn(semanticTokens.positive.bg, semanticTokens.positive.fg)
                      : cn(semanticTokens.warning.bg, semanticTokens.warning.fg)
                  )}
                >
                  {account.status || 'Unknown'}
                </Badge>
              </div>
            )}
          </div>

          {/* Footer - Sticky */}
          <SheetFooter className={cn(
            'flex-shrink-0 border-t pt-4 pb-6 gap-3'
          )}>
            <Button
              variant="outline"
              onClick={onClose}
              className={cn(
                'flex-1 min-h-[52px] rounded-xl',
                semanticTokens.motion.standard
              )}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className={cn(
                'flex-1 min-h-[52px] rounded-xl gap-2',
                semanticTokens.motion.standard
              )}
            >
              {isSaving ? (
                <InlineLoadingIndicator size="sm" aria-hidden="true" />
              ) : (
                <SFIcon icon={sfIcons.check} className="h-4 w-4" aria-hidden="true" />
              )}
              Save changes
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Deactivate Confirmation */}
      <AlertDialog open={showDeactivateConfirm} onOpenChange={setShowDeactivateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <SFIcon icon={sfIcons.warning} className="h-5 w-5 text-orange-500" />
              Deactivate account?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate the bank account. You won't be able to receive withdrawals to this account until you reactivate it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleToggleStatus}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isToggling ? (
                <InlineLoadingIndicator size="sm" className="mr-2" />
              ) : null}
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <SFIcon icon={sfIcons.delete} className="h-5 w-5 text-destructive" />
              Delete account?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The bank account will be permanently removed from your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? (
                <InlineLoadingIndicator size="sm" className="mr-2" />
              ) : null}
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
